import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { Alert, Card, Dropdown, Grid, Icon, InputSelect, InputText, Modal, Picture, Spinner, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, StatusStateEnum } from "~/types/enums";
import InfiniteScroll from "react-infinite-scroll-component";
import footprintSelectors from "~/store/footprints/selectors";
import footprintActionCreators from "~/store/footprints/actions";
import Footprint from "~/components/Footprint";
import FootprintSelectionCard from "~/components/FootprintSelectionCard";
import { FootprintActionEnum } from "~/store/footprints/types";
import FootprintBulkTestDetectionModal from "~/components/FootprintBulkTestDetectionModal";
import FootprintBulkDeleteModal from "~/components/FootprintBulkDeleteModal";
import Mark from "mark.js";
import FootprintBulkMoveModal from "~/components/FootprintBulkMoveModal";
import styled, { css, useTheme } from "styled-components";
import process from "~/assets/i/illustrations/process.svg";
import Markdown from "~/components/Markdown";
import InputTopicSelect from "~/components/InputTopicSelect";
import InputMemberSelect from "~/components/InputMemberSelect";
import lists from "~/lib/lists";
import useAbacExt from "~/hooks/abac";
import FootprintGroup from "~/components/FootprintGroup";
import { initialFilter } from "~/store/footprints/reducer";
import { useIntegratedSources } from "~/hooks/integrations";
import FootprintsStatusAlert from "~/components/FootprintsStatusAlert";
const StyledCard = styled(Card)`
  .topic__footprint-target,
  .topic__footprint,
  .topic__footprint-group {
    border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
    transition: background-color 350ms ease-in-out;
    padding: 10px 0;

    .topic__footprint-checkbox,
    .topic__footprint-absolute-date,
    .topic__footprint-actions {
      transition: opacity 350ms ease-in-out;
      opacity: 0;
    }

    .topic__footprint-timeline:before {
      transition: background-color 350ms ease-in-out;
      background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.secondary;
}};
    }

    &--hovered,
    &--selected {
      background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.background.primary;
}};

      .topic__footprint-checkbox {
        opacity: 1;
      }

      .topic__footprint-timeline:before {
        background-color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.background.primary;
}};
      }
    }

    &--hovered {
      .topic__footprint-absolute-date,
      .topic__footprint-actions {
        opacity: 1;
      }
    }
  }
`;
const StyledGrid = styled(Grid)`
  overflow: hidden;
  position: relative;

  ${_ref5 => {
  let {
    $loading
  } = _ref5;
  return $loading && css`
      cursor: pointer;

      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${_ref6 => {
    let {
      theme
    } = _ref6;
    return theme.colors.background.primary;
  }};
        opacity: 0.6;
        z-index: 1;
        transition: background-color 200ms ease-out;
      }
    `;
}}
`;
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const FootprintsCard = _ref7 => {
  let {
    ...rest
  } = _ref7;
  const footprints = useSelector(footprintSelectors.getFootprints);
  const status = useSelector(footprintSelectors.getStatus);
  const activeSources = useIntegratedSources();
  const sources = Object.keys(lists.topics.footprintSources.names).map(source => ({
    id: source,
    value: lists.topics.footprintSources.names[source],
    hidden: !activeSources.includes(source)
  }));
  const action = useSelector(footprintSelectors.getAction);
  const filter = useSelector(footprintSelectors.getFilter);
  const {
    hasAfter
  } = useSelector(footprintSelectors.getPagination);
  const {
    colors
  } = useTheme();
  const error = useSelector(footprintSelectors.getError);
  const [showExplanation, setShowExplanation] = useState(false);
  const selectedData = useSelector(footprintSelectors.getSelectedData);
  const {
    userHas
  } = useAbacExt();
  const selection = selectedData?.footprintIds || [];
  const isFiltering = !!filter.memberIds?.length || !!filter.topicIds?.length || !!filter.sources?.length || !!filter.query || false;
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(footprintActionCreators.fetchStarted());
    }
  }, [dispatch, status]);
  useEffect(() => {
    const markInstance = new Mark(document.querySelectorAll(".search-content"));
    markInstance.unmark({
      done() {
        markInstance.mark(filter.query, {
          separateWordSearch: false
        });
      }
    });
  }, [filter.query]);
  useEffect(() => {
    return () => {
      dispatch(footprintActionCreators.filtered({
        ...initialFilter
      }));
    };
  }, []);
  const handleLoadMore = () => {
    dispatch(footprintActionCreators.loadedMore());
  };
  const handleFilter = newFilter => {
    dispatch(footprintActionCreators.filtered({
      ...filter,
      ...newFilter
    }));
  };
  const handleRunRetro = () => {
    dispatch(footprintActionCreators.actionChanged(FootprintActionEnum.RUN_RETRO, null));
  };
  const handleToggleSelection = function (ids) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let sel = selection;
    if (!force) {
      const isAllSelected = ids.every(id => sel.includes(id));
      if (isAllSelected) {
        sel = sel.filter(id => !ids.includes(id));
      } else {
        const newValues = [...sel, ...ids];
        sel = newValues.filter((item, pos) => {
          return newValues.indexOf(item) === pos;
        });
      }
    } else {
      sel = ids;
    }
    dispatch(footprintActionCreators.actionChanged(null, null, {
      footprintIds: sel,
      count: sel.length
    }));
  };
  if (!isFiltering && !footprints.length && status === StatusStateEnum.SUCCEEDED) {
    return /*#__PURE__*/React.createElement(Grid, {
      cols: 1,
      justify: "center",
      s: {
        mt: 10
      }
    }, /*#__PURE__*/React.createElement(Picture, {
      src: process,
      width: "300px",
      height: "200px",
      border: "squared",
      backgroundSize: "contain"
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      weight: "semiBold",
      display: "block",
      s: {
        mt: 5
      }
    }, t("Footprints.noFootprints")), /*#__PURE__*/React.createElement(Markdown, {
      color: "textSecondary",
      fontSize: 4,
      lineHeight: 6,
      display: "block",
      s: {
        mt: 3,
        mb: 5
      },
      style: {
        maxWidth: "500px"
      },
      align: "center",
      text: t("Footprints.noFootprintsInfo")
    }));
  }
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 0
    },
    fullWidth: true
  }, rest), /*#__PURE__*/React.createElement(StyledCard, {
    s: {
      p: 0
    }
  }, /*#__PURE__*/React.createElement(InfiniteScroll, {
    style: scrollPaneStyle,
    dataLength: footprints.length || 0,
    next: handleLoadMore,
    hasMore: hasAfter,
    loader: /*#__PURE__*/React.createElement(Spinner, {
      center: true
    })
  }, /*#__PURE__*/React.createElement(FootprintsStatusAlert, null), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 3,
    template: "1fr 180px 180px 180px 180px 3.1rem",
    s: {
      mb: 3
    }
  }, /*#__PURE__*/React.createElement(InputText, {
    value: filter?.query || "",
    placeholder: t("Footprints.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    onChange: value => handleFilter({
      ...filter,
      query: value
    }),
    color: "translucent",
    clearButton: true
  }), /*#__PURE__*/React.createElement(InputTopicSelect, {
    color: "translucent",
    value: filter?.topicIds,
    onChange: value => handleFilter({
      ...filter,
      topicIds: value
    }),
    placeholder: t("Footprints.allTopics"),
    multiple: true
  }), /*#__PURE__*/React.createElement(InputMemberSelect, {
    color: "translucent",
    value: filter?.memberIds,
    onChange: value => handleFilter({
      ...filter,
      memberIds: value
    }),
    placeholder: t("Footprints.allMembers"),
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    options: sources,
    value: filter?.sources,
    onChange: value => handleFilter({
      ...filter,
      sources: value
    }),
    color: "translucent",
    placeholder: t("Footprints.allSources"),
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    options: lists.footprints.statuses.names,
    value: filter?.statuses,
    onChange: value => handleFilter({
      ...filter,
      statuses: value
    }),
    color: "translucent",
    placeholder: t("Footprints.allStatuses"),
    multiple: true
  }), /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      s: {
        p: 2.8
      },
      color: "translucent",
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    })
  }, _ref8 => {
    let {
      isOpen,
      onItemClick
    } = _ref8;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHas([AuthoritiesEnum.EXPERIMENTAL]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        setShowExplanation(prev => !prev);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "encrypted",
      s: {
        mr: 2
      },
      color: "primary"
    }), showExplanation ? t("FootprintsCard.hideMetadata") : t("FootprintsCard.showMetadata")), /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleRunRetro();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "searchHistory",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("FootprintsCard.runRetro")));
  })), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 4
    },
    fullWidth: true
  }, error), footprints.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    $loading: status === StatusStateEnum.LOADING,
    cols: 1,
    template: "1fr",
    gap: 3,
    fullWidth: true,
    s: {
      mb: 6,
      pt: 2
    }
  }, footprints.map(footprint => filter.isGrouped && footprint.children?.length ? /*#__PURE__*/React.createElement(FootprintGroup, {
    key: footprint.id,
    footprints: [footprint, ...footprint.children],
    selection: selection,
    setSelection: handleToggleSelection
  }) : /*#__PURE__*/React.createElement(Footprint, {
    key: footprint.id,
    footprint: footprint,
    selection: selection,
    showExplanation: showExplanation,
    setSelection: handleToggleSelection,
    hideActions: !userHas(AuthoritiesEnum.FOOTPRINTS_WRITE)
  }))) : status === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    color: "textSecondary",
    align: "center",
    s: {
      p: 4
    }
  }, t("Footprints.noFootprintsFound")))), !!selection.length && /*#__PURE__*/React.createElement(FootprintSelectionCard, {
    selection: selection,
    selectionCount: selection.length,
    setSelection: handleToggleSelection
  }), userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Modal, {
    open: action === FootprintActionEnum.TEST_DETECTION
  }, /*#__PURE__*/React.createElement(FootprintBulkTestDetectionModal, null)), userHas(AuthoritiesEnum.FOOTPRINTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === FootprintActionEnum.BULK_DELETE
  }, /*#__PURE__*/React.createElement(FootprintBulkDeleteModal, null)), userHas(AuthoritiesEnum.FOOTPRINTS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === FootprintActionEnum.BULK_MOVE
  }, /*#__PURE__*/React.createElement(FootprintBulkMoveModal, null)));
};
export default FootprintsCard;